/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from "react";
import Fuse from "fuse.js";
import * as S from "./styles";

export interface DataProps {
  label: string;
  value: string;
}

export interface Props {
  label?: string;
  data: DataProps[];
  value: DataProps | null;
  onChange: (val: DataProps | null) => void;
  disabled?: boolean;
  dropdownMaxHeight?: number;
}

const SelectAutoComplete: React.FC<Props> = ({
  data,
  value,
  onChange,
  label = "",
  disabled = false,
  dropdownMaxHeight = 200,
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [isExiting, setIsExiting] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [results, setResults] = useState<DataProps[]>(data);
  const [dropdownAbove, setDropdownAbove] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (value) {
      setInputValue(value?.label);
    }
  }, [value]);

  useEffect(() => {
    if (data) {
      setResults(data);
    }
  }, [data]);

  useEffect(() => {
    if (isDropdownVisible) {
      const containerRect = containerRef.current?.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (containerRect) {
        const spaceBelow = viewportHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;

        if (spaceBelow < dropdownMaxHeight && spaceAbove > dropdownMaxHeight) {
          setDropdownAbove(true);
        } else {
          setDropdownAbove(false);
        }
      }
    }
  }, [isDropdownVisible, dropdownMaxHeight]);

  const fuse = new Fuse(data, {
    keys: ["label"],
    threshold: 0.2,
    distance: 100,
    includeScore: true,
  });

  const handleInputChange = (input: string) => {
    setInputValue(input);
    if (input.trim() === "") {
      setResults(data);
    } else {
      const searchResults = fuse.search(input);
      setResults(searchResults.map((result) => result.item));
    }
  };

  const handleFocus = () => {
    setIsExiting(false);
    setIsDropdownVisible(true);
    setIsFocused(true);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (containerRef.current?.contains(event.relatedTarget as Node)) {
      return;
    }
    const matchedItems = data.filter(
      (item: any) => item.label === inputValue || item.value === inputValue
    );
    if (matchedItems.length > 0) {
      setInputValue(matchedItems[0].label);
      onChange(matchedItems[0]);
    } else {
      setInputValue("");
      onChange(null);
      setTimeout(() => {
        setResults(data);
      }, 150);
    }

    setIsFocused(false);
    setIsExiting(true);
    setTimeout(() => {
      setIsDropdownVisible(false);
      setIsExiting(false);
    }, 150);
  };

  const handleDropdownMouseDown = (event: React.MouseEvent) => {
    event.preventDefault(); // Prevents the input from losing focus
  };

  const handleDropdownItemClick = (item: DataProps) => {
    setInputValue(item.label);
    onChange(item);

    setTimeout(() => {
      inputRef.current?.blur();
    }, 150);
  };

  useEffect(() => {
    if (value === null) {
      setInputValue("");
    }
  }, [value]);

  return (
    <S.Wrapper ref={containerRef}>
      {label ? <S.Label>{label}</S.Label> : null}
      <S.Container disabled={disabled} isFocused={isFocused}>
        <S.Input
          ref={inputRef}
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange(e.target.value)
          }
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </S.Container>
      {isDropdownVisible ? (
        <S.Dropdown
          isExiting={isExiting}
          onMouseDown={handleDropdownMouseDown}
          dropdownMaxHeight={dropdownMaxHeight}
          dropdownAbove={dropdownAbove}
        >
          {results.map((item) => (
            <S.DropdownItem
              key={item.value}
              onClick={() => handleDropdownItemClick(item)}
              selected={item === value}
            >
              {item.label}
            </S.DropdownItem>
          ))}
        </S.Dropdown>
      ) : null}
    </S.Wrapper>
  );
};

export default SelectAutoComplete;
